import { readCookie } from "@qgiv/core-donor";
import { constants } from "@qgiv/core-js";
import { isEqual } from "lodash";

/**
 * @typedef {import("@qgiv/donation-form").UtilityTypes.AbandonedGiftTypes.FormSettingsToEvaluate} FormSettingsToEvaluate
 */

const {
    ENUMS: { FormTemplate },
} = constants;

/**
 * @function getFormSpecificPathForSettingCookie
 * @description Determines the specific path for setting a cookie
 * @param {object} params
 * @param {number} params.embedId embed id from config, forced to be 0 for local embed
 * @param {object} params.eventSettings will be empty object when year round form
 * @param {object} params.formSettings
 * @param {string} params.path path value from config
 * @returns {string} The specific path for setting the cookie.
 */
export const getFormSpecificPathForSettingCookie = ({
    embedId = 0,
    eventSettings = {},
    formSettings = {},
    path = "",
}) => {
    const { template = 0 } = formSettings;
    const { alias = "" } = eventSettings;
    const isEmbeddedExternally = embedId > 0;
    const isStandardYearRoundForm = template === FormTemplate.NEW_STANDARD_FORM;
    const isP2PForm = template === FormTemplate.P2P_STANDARD;

    // we don't want to do anything specific with setting the cookie path on external sites
    if (isEmbeddedExternally) {
        return "/";
    }
    if (isStandardYearRoundForm) {
        return `/for/${path}`;
    }
    if (isP2PForm) {
        return `/event/${alias}`;
    }
    // default case of empty string will cause cookie to not be set
    // in elements.development.js
    return "";
};

/**
 * @function getFormSettingsToEvaluate
 * @description returns a subset of form settings needed to evaluate if able to load abandoned gift data
 * @param {object} formSettings
 * @returns {FormSettingsToEvaluate}
 */
export const getFormSettingsToEvaluate = (formSettings) => {
    const {
        appearance,
        donationActive,
        enableRecur,
        feeCoverage,
        feeCoverageFlat,
        feeCoveragePercent,
        id,
        restrictionGivingType,
    } = formSettings;
    return {
        appearance,
        donationActive,
        enableRecur,
        feeCoverage,
        feeCoverageFlat,
        feeCoveragePercent,
        id,
        restrictionGivingType,
    };
};

/**
 * @function removeEmptyValueKeysFromObject
 * @description removes empty key/value pairs to replicate data being sent with qfetch
 * @param {object} objectToRemoveEmptyKeys
 * @returns {object}
 */
export const removeEmptyValueKeysFromObject = (objectToRemoveEmptyKeys) => {
    const keysOfObjectToRemoveEmptyKeys = Object.keys(objectToRemoveEmptyKeys);
    const objectWithEmptyKeysRemoved = keysOfObjectToRemoveEmptyKeys.reduce(
        (allFormSettings, currentSettingKey) => {
            const currentSettingValue =
                objectToRemoveEmptyKeys[currentSettingKey];
            // there might be more instances we need to flush out
            if (
                Array.isArray(currentSettingValue) &&
                currentSettingValue.length === 0
            ) {
                return allFormSettings;
            }
            if (currentSettingValue === undefined) {
                return allFormSettings;
            }
            return {
                ...allFormSettings,
                [currentSettingKey]: objectToRemoveEmptyKeys[currentSettingKey],
            };
        },
        {},
    );
    return objectWithEmptyKeysRemoved;
};

/**
 * @function getFormSettingsAreEqual
 * @description uses isEqual to compare form settings from current instance and when gift was abandoned
 * @param {object} params
 * @param {object} params.comparisonFormAndDonationSettings
 * @param {object} params.storedAbandonedGiftData
 * @returns {boolean}
 */
export const getFormSettingsAreEqual = ({
    comparisonFormAndDonationSettings = {},
    storedAbandonedGiftData = {},
}) => {
    const { formAndDonationSettingsDuringAbandonedGift = {} } =
        storedAbandonedGiftData;

    const oldFormSettings =
        formAndDonationSettingsDuringAbandonedGift.formSettingsToEvaluate || {};

    const allNewFormSettings =
        comparisonFormAndDonationSettings.formSettings || {};

    const newFormSettingsToEvaluate =
        getFormSettingsToEvaluate(allNewFormSettings);

    const newFormSettingsWithoutEmptyValues = removeEmptyValueKeysFromObject(
        newFormSettingsToEvaluate,
    );

    const formSettingsAreEqual = isEqual(
        newFormSettingsWithoutEmptyValues,
        oldFormSettings,
    );

    return formSettingsAreEqual;
};

/**
 * @function getDonationSettingsAreEqual
 * @description uses isEqual to compare donation settings from current instance and when gift was abandoned
 * @param {object} params
 * @param {object} params.comparisonFormAndDonationSettings
 * @param {object} params.storedAbandonedGiftData
 * @returns {boolean}
 */
export const getDonationSettingsAreEqual = ({
    comparisonFormAndDonationSettings = {},
    storedAbandonedGiftData = {},
}) => {
    const { formAndDonationSettingsDuringAbandonedGift = {} } =
        storedAbandonedGiftData;

    const oldDonationSettings =
        formAndDonationSettingsDuringAbandonedGift.donationSettings || {};

    const newDonationSettings =
        comparisonFormAndDonationSettings.donationSettings || {};

    const newDonationSettingsWithoutRecurringUpgradeFields = {
        ...newDonationSettings,
        recurringUpgradeModalFields: [],
    };

    const newDonationSettingsWithoutEmptyValues =
        removeEmptyValueKeysFromObject(
            newDonationSettingsWithoutRecurringUpgradeFields,
        );

    const donationSettingsAreEqual = isEqual(
        newDonationSettingsWithoutEmptyValues,
        oldDonationSettings,
    );

    return donationSettingsAreEqual;
};

/**
 * @function getFormAndDonationSettingsAreEqual
 * @description combines output of getDonationSettingsAreEqual and getFormSettingsAreEqual
 * @param {object} params
 * @param {object} params.comparisonFormAndDonationSettings
 * @param {object} params.storedAbandonedGiftData
 * @returns {boolean}
 */
export const getFormAndDonationSettingsAreEqual = ({
    comparisonFormAndDonationSettings = {},
    storedAbandonedGiftData = {},
}) => {
    const donationSettingsAreEqual = getDonationSettingsAreEqual({
        comparisonFormAndDonationSettings,
        storedAbandonedGiftData,
    });
    const formSettingsAreEqual = getFormSettingsAreEqual({
        comparisonFormAndDonationSettings,
        storedAbandonedGiftData,
    });
    const formAndDonationSettingsAreEqual =
        donationSettingsAreEqual && formSettingsAreEqual;
    return formAndDonationSettingsAreEqual;
};

/**
 * @function getFormAndDonationSettingsDuringAbandonedGift
 * @description get settings for later evaluation of abandoned gift data
 * @param {object} params
 * @param {object} params.donationSettings
 * @param {object} params.formSettings
 * @returns {object}
 */
export const getFormAndDonationSettingsDuringAbandonedGift = ({
    donationSettings,
    formSettings,
}) => {
    const formSettingsToEvaluate = getFormSettingsToEvaluate(formSettings);
    // whenever donation settings are saved, new id's are created for
    // recurringUpgradeModalFields?
    const donationSettingsWithoutRecurringUpgradeFields = {
        ...donationSettings,
        recurringUpgradeModalFields: [],
    };

    const formAndDonationSettingsDuringAbandonedGift = {
        formSettingsToEvaluate,
        donationSettings: donationSettingsWithoutRecurringUpgradeFields,
    };

    return formAndDonationSettingsDuringAbandonedGift;
};

/**
 * @function getFormikValuesForAbandonedGiftData
 * @description returns sanitized formik values object
 * @param {object} values
 * @returns {object}
 */
const getFormikValuesForAbandonedGiftData = (values) => {
    const allowedFormikValuesKeyNames = [
        "Address",
        "Address_2",
        "City",
        "Company",
        "Company_Donation",
        "Country",
        "Email",
        "First_Name",
        "Last_Name",
        "Phone",
        "State",
        "Zip",
    ];
    const valuesKeys = Object.keys(values);
    const allowedFormikValues = valuesKeys.reduce(
        (allAllowedValues, currentValueKey) => {
            if (allowedFormikValuesKeyNames.includes(currentValueKey)) {
                return {
                    ...allAllowedValues,
                    [currentValueKey]: values[currentValueKey],
                };
            }
            return allAllowedValues;
        },
        {},
    );
    return allowedFormikValues;
};

/**
 * @function prepareDataForAbandonedGiftSubmission
 * @description sanitizes redux and formik values, gathers settings for storing abandoned gift data
 * @param {object} params
 * @param {object} params.abandonedGiftReduxData
 * @param {object} params.donationSettings
 * @param {object} params.formSettings
 * @param {object} params.values
 * @returns {object}
 */
export const prepareDataForAbandonedGiftSubmission = ({
    abandonedGiftReduxData,
    donationSettings,
    formSettings,
    values,
}) => {
    const formAndDonationSettingsDuringAbandonedGift =
        getFormAndDonationSettingsDuringAbandonedGift({
            donationSettings,
            formSettings,
        });

    const allowedFormikValues = getFormikValuesForAbandonedGiftData(values);

    const abandonedGiftData = {
        ...abandonedGiftReduxData,
        formAndDonationSettingsDuringAbandonedGift,
        donationDetails: {
            ...abandonedGiftReduxData.donationDetails,
            // we don't want to store custom field values for potential issues with PII
            customFields: {},
            dedication: {},
            matching: {},
            // these should not get stored values in redux currently
            // until we allow payment page to move around in pageOrder
            billing: {},
            payment: {},
        },
        allowedFormikValues,
    };
    return abandonedGiftData;
};

/**
 * @function getEmbedUrl
 * @description gets embed url to later generate url abandoned gift data email url
 * @param {object} params
 * @param {object} params.config
 * @param {object} params.eventSettings
 * @param {number} params.template
 * @returns {string} embedUrl
 */
export const getEmbedUrl = ({ config = {}, eventSettings = {}, template }) => {
    const { embed, path, HTTP_HOST } = config;
    const { alias = "" } = eventSettings;
    const isEmbeddedLocally = embed === null;
    let embedUrl = "";

    if (template === FormTemplate.NEW_STANDARD_FORM && isEmbeddedLocally) {
        embedUrl = `${HTTP_HOST}/for/${path}`;
    }

    if (template === FormTemplate.P2P_STANDARD && isEmbeddedLocally) {
        embedUrl = `${HTTP_HOST}/event/${alias}`;
    }

    if (!isEmbeddedLocally) {
        const { url = "" } = embed;
        embedUrl = url;
    }

    return embedUrl;
};

/**
 * @function getHasPreviouslyAcceptedReminderEmailValue
 * @description evaluates qgiv_pare cookie
 * @returns {boolean} hasPreviouslyAcceptedReminderEmailValue
 */
export const getHasPreviouslyAcceptedReminderEmailValue = () => {
    const cookie = readCookie("qgiv_pare");
    const cookieExists = !!cookie;
    let hasPreviouslyAcceptedReminderEmailValue = false;
    if (cookieExists) {
        const data = JSON.parse(cookie);
        const { hasPreviouslyAcceptedReminderEmail = false } = data;
        hasPreviouslyAcceptedReminderEmailValue =
            hasPreviouslyAcceptedReminderEmail;
    }
    return hasPreviouslyAcceptedReminderEmailValue;
};

/**
 * @function getHasAbandonedGiftSourceParam
 * @description searches url for abandonedsource param
 * @returns {boolean} hasAbandonedGiftSourceParam
 */
export const getHasAbandonedGiftSourceParam = () => {
    const params = new URLSearchParams(window.location.search);
    const hasAbandonedGiftSourceParam = params.has("abandonedsource");
    return hasAbandonedGiftSourceParam;
};

/**
 * @function getHasAbandonedGiftTokenParam
 * @description searches url for abandonedGiftToken param
 * @returns {boolean} hasAbandonedGiftTokenParam
 */
export const getHasAbandonedGiftTokenParam = () => {
    const params = new URLSearchParams(window.location.search);
    const hasAbandonedGiftTokenParam = params.has("abandonedGiftToken");
    return hasAbandonedGiftTokenParam;
};

/**
 * @function getHasPreviouslyAcceptedOrCurrentlyIsOnReminderEmail
 * @description evaluates qgiv_pare cookie and current url to establish
 * exit intent behavior for donation form modal.
 * @returns {boolean} hasPreviouslyAcceptedOrCurrentlyOnReminderEmail
 */
export const getHasPreviouslyAcceptedOrCurrentlyIsOnReminderEmail = () => {
    const hasPreviouslyAcceptedReminderEmailValue =
        getHasPreviouslyAcceptedReminderEmailValue();
    const hasAbandonedGiftTokenParam = getHasAbandonedGiftTokenParam();

    const hasPreviouslyAcceptedOrCurrentlyOnReminderEmail =
        hasPreviouslyAcceptedReminderEmailValue || hasAbandonedGiftTokenParam;
    return hasPreviouslyAcceptedOrCurrentlyOnReminderEmail;
};
